import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { useFilesAndFoldersQuery } from 'api/queries/useFilesAndFoldersQuery';
import { FileItem } from 'api/types';
import { ReactComponent as SettingsIcon } from 'assets/icons/navbar/settings.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/searchIcon.svg';
import startLogo from 'assets/images/startLogo.svg';
import { CompanySelector } from 'features/companySelector';
import { FilePreviewModal } from 'features/documents/components/FilePreviewModal';
import { LanguageSelector } from 'features/languageSelector';
import { NotificationPreview } from 'features/notifications';
import { useAuth, useWindowSize } from 'hooks';
import { Links } from 'settings';

import {
  ClearSearchBtn,
  LogoImg,
  LogoW,
  MobileRow,
  MobileTopBar,
  RightIconsInnerW,
  RightIconsW,
  SearchDropdown,
  SearchDropdownEmpty,
  SearchDropdownItem,
  SearchInput,
  SearchW,
  StyledBtn,
  TopBarW,
} from './styled';

export function TopMenu(): ReactElement {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isManager } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedQ] = useDebounce(searchValue, 300);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [isMenuHidden, setIsMenuHidden] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (!isMobile) return;

    const el = document.getElementById('main-page');
    if (!el) return;

    const handleScroll = () => {
      const current = el.scrollTop;
      if (current > lastScrollY.current && current > 60) {
        setIsMenuHidden(true);
      } else {
        setIsMenuHidden(false);
      }
      lastScrollY.current = current;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const { files } = useFilesAndFoldersQuery({ q: debouncedQ || undefined });

  const showDropdown = isOpen && !!debouncedQ;

  const searchWidget = (
    <SearchW ref={searchRef} style={{ position: 'relative' }}>
      <SearchIcon style={{ width: '24px', height: '24px' }} />
      <SearchInput
        placeholder={t('topMenu.searchPlaceholder')}
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
      />
      {searchValue && (
        <ClearSearchBtn
          onMouseDown={(e) => {
            e.preventDefault();
            setSearchValue('');
            setIsOpen(false);
          }}
        >
          ✕
        </ClearSearchBtn>
      )}
      {showDropdown && (
        <SearchDropdown>
          {files.length === 0 ? (
            <SearchDropdownEmpty>{t('document.list.emptyFiles')}</SearchDropdownEmpty>
          ) : (
            files.map((file) => (
              <SearchDropdownItem
                key={file.id}
                onClick={() => {
                  setSelectedFile(file);
                  setIsOpen(false);
                }}
              >
                {file.display_name}
              </SearchDropdownItem>
            ))
          )}
        </SearchDropdown>
      )}
    </SearchW>
  );

  if (isMobile) {
    return (
      <>
        <MobileTopBar hidden={isMenuHidden}>
          <MobileRow>
            <LogoW
              onClick={() => navigate({ pathname: Links.we.index })}
              style={{ cursor: 'pointer' }}
            >
              <LogoImg src={startLogo} alt="Adligo logo" />
            </LogoW>
            <RightIconsInnerW style={{ marginLeft: 'auto' }}>
              <NotificationPreview />
              {isManager && (
                <StyledBtn onClick={() => navigate({ pathname: Links.settings.index })}>
                  <SettingsIcon style={{ width: 24, height: 24 }} />
                </StyledBtn>
              )}
              {!pathname.startsWith('/resources/') && <LanguageSelector />}
            </RightIconsInnerW>
          </MobileRow>
          <MobileRow>
            {searchWidget}
            <CompanySelector />
          </MobileRow>
        </MobileTopBar>

        {selectedFile && (
          <FilePreviewModal
            file={selectedFile}
            open={!!selectedFile}
            onClose={() => setSelectedFile(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <TopBarW isMobile={isMobile}>
        <LogoW onClick={() => navigate({ pathname: Links.we.index })} style={{ cursor: 'pointer' }}>
          <LogoImg src={startLogo} alt="Adligo logo" />
        </LogoW>

        {searchWidget}

        <RightIconsW>
          <CompanySelector />
          <RightIconsInnerW>
            <NotificationPreview />
            {isManager && (
              <StyledBtn onClick={() => navigate({ pathname: Links.settings.index })}>
                <SettingsIcon style={{ width: 24, height: 24 }} />
              </StyledBtn>
            )}
            {!pathname.startsWith('/resources/') && <LanguageSelector />}
          </RightIconsInnerW>
        </RightIconsW>
      </TopBarW>

      {selectedFile && (
        <FilePreviewModal
          file={selectedFile}
          open={!!selectedFile}
          onClose={() => setSelectedFile(null)}
        />
      )}
    </>
  );
}
